import React, { useId, useState } from "react";

import { XIcon } from "lucide-react";
import { useController, useFormContext } from "react-hook-form";

import { Badge } from "@/src/components/ui/badge";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@/src/components/ui/field";
import { cn } from "@/src/lib/utils";

import type { ChangeEvent, ClipboardEvent, JSX, KeyboardEvent } from "react";
import type { FieldPathByValue, FieldValues } from "react-hook-form";

interface ChipInputFieldBaseProps {
  label?: string;
  description?: string;
  placeholder?: string;
  value: string[];
  disabled?: boolean;
  error?: string;
  normalize?: (value: string) => string;
  validate?: (value: string) => string | null;
  getRemoveLabel: (value: string) => string;
  onValueChange: (value: string[]) => void;
}

const SPLIT_REGEX = /[\s,]+/;

export function ChipInputFieldBase({
  label,
  description,
  placeholder,
  value,
  disabled,
  error,
  normalize,
  validate,
  getRemoveLabel,
  onValueChange,
}: ChipInputFieldBaseProps): JSX.Element {
  const id = useId();
  const [draft, setDraft] = useState("");

  const addValueList = (rawValueList: string[]) => {
    const nextValueList = [...value];
    rawValueList.forEach((rawValue) => {
      const trimmedValue = rawValue.trim();
      const chip = normalize ? normalize(trimmedValue) : trimmedValue;
      if (chip && !nextValueList.includes(chip)) {
        nextValueList.push(chip);
      }
    });
    if (nextValueList.length !== value.length) {
      onValueChange(nextValueList);
    }
  };

  const commitDraft = () => {
    addValueList(draft.split(SPLIT_REGEX));
    setDraft("");
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      commitDraft();
    } else if (event.key === "Backspace" && !draft && value.length > 0) {
      onValueChange(value.slice(0, -1));
    }
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedValueList = event.clipboardData
      .getData("text")
      .split(SPLIT_REGEX);
    // A single value is inserted into the text being typed, like a normal paste
    if (pastedValueList.length < 2) return;
    event.preventDefault();
    addValueList(pastedValueList);
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) =>
    setDraft(event.target.value);

  const handleRemove = (chip: string) =>
    onValueChange(value.filter((item) => item !== chip));

  const chipErrorList = value.map((chip) => validate?.(chip) ?? null);
  const messageList = [
    ...new Set([error, ...chipErrorList].filter((message) => !!message)),
  ];

  return (
    <Field>
      {label ? <FieldLabel htmlFor={id}>{label}</FieldLabel> : null}
      <div
        className={cn(
          "border-input focus-within:border-ring focus-within:ring-ring/50 flex min-h-9 w-full flex-wrap items-center gap-1.5 rounded-md border bg-transparent px-2 py-1.5 shadow-xs transition-[color,box-shadow] focus-within:ring-[3px]",
          error && "border-destructive ring-destructive/20",
          disabled && "pointer-events-none opacity-50",
        )}
      >
        {value.map((chip, index) => {
          const chipError = chipErrorList[index];

          return (
            <Badge
              key={chip}
              aria-invalid={chipError ? true : undefined}
              className="gap-1 pr-1"
              variant={chipError ? "destructive" : "secondary"}
            >
              {chip}
              <button
                aria-label={getRemoveLabel(chip)}
                className="rounded-sm opacity-70 hover:opacity-100"
                disabled={disabled}
                onClick={() => handleRemove(chip)}
                type="button"
              >
                <XIcon />
              </button>
            </Badge>
          );
        })}
        <input
          aria-invalid={error ? true : undefined}
          className="placeholder:text-muted-foreground min-w-24 flex-1 bg-transparent text-sm outline-none"
          disabled={disabled}
          id={id}
          onBlur={commitDraft}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          placeholder={value.length === 0 ? placeholder : undefined}
          value={draft}
        />
      </div>
      {description ? <FieldDescription>{description}</FieldDescription> : null}
      {messageList.map((message) => (
        <FieldError key={message}>{message}</FieldError>
      ))}
    </Field>
  );
}

interface ChipInputFieldProps<T extends FieldValues> {
  name: FieldPathByValue<T, string[]>;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  normalize?: (value: string) => string;
  validate?: (value: string) => string | null;
  getRemoveLabel: (value: string) => string;
}

export function ChipInputField<T extends FieldValues>({
  name,
  label,
  description,
  placeholder,
  disabled,
  normalize,
  validate,
  getRemoveLabel,
}: ChipInputFieldProps<T>): JSX.Element {
  const { control } = useFormContext();
  const { field, fieldState } = useController({ control, name });

  return (
    <ChipInputFieldBase
      description={description}
      disabled={disabled}
      error={fieldState.error?.message}
      getRemoveLabel={getRemoveLabel}
      label={label}
      normalize={normalize}
      onValueChange={field.onChange}
      placeholder={placeholder}
      validate={validate}
      value={field.value ?? []}
    />
  );
}
